import {
    AxisChartSeriesDataPoint,
    AxisScale,
    ChartRange,
    Point,
} from 'shared/chart/core/types';
import { Bounds } from 'shared/chart/shared/Bounds';

export const log = (value: number) => (value ? Math.log(value) : 0);

export const exp = (value: number) => Math.exp(value);

export const constrainValue = (value: number, min: number, max: number) =>
    min > max ? Math.max(Math.min(value, min), max) : Math.min(Math.max(value, min), max);

export const isValueInRange = (value: number, min: number, max: number) =>
    value >= Math.min(min, max) && value <= Math.max(min, max);

export const isValueInChartRange = (value: number, range: ChartRange) =>
    isValueInRange(value, range.min, range.max);

export const isChartRangeEmpty = (range: ChartRange) => range.min === range.max;

export const isFullRangeInChartRange = (min: number, max: number, range: ChartRange) =>
    isValueInChartRange(min, range) && isValueInChartRange(max, range);

export const isZoomedIn = (
    range: ChartRange | undefined,
    zoomRange: ChartRange | undefined
) => {
    if (!range || !zoomRange) {
        return false;
    }
    return !chartRangesAreEqual(range, zoomRange);
};

export const chartRangesAreEqual = (
    range1: ChartRange | undefined,
    range2: ChartRange | undefined
) => {
    if (range1 === range2) {
        return true;
    }
    if (range1 && range2) {
        return range1.min === range2.min && range1.max === range2.max;
    }
    return false;
};

export const pointsAreEqual = (point1: Point, point2: Point, tolerance = 0) =>
    Math.abs(point1.x - point2.x) < tolerance &&
    Math.abs(point1.y - point2.y) < tolerance;

export const mapValue = (
    value: number,
    fromStart: number,
    fromEnd: number,
    toStart: number,
    toEnd: number
) => {
    if (fromStart === toStart && fromEnd === toEnd) {
        return value;
    }
    if (fromEnd === fromStart) {
        return (toEnd - toStart) / 2;
    }
    return (toEnd - toStart) * ((value - fromStart) / (fromEnd - fromStart)) + toStart;
};

export const mapAndConstrainValue = (
    value: number,
    fromStart: number,
    fromEnd: number,
    toStart: number,
    toEnd: number
) =>
    mapValue(
        constrainValue(value, fromStart, fromEnd),
        fromStart,
        fromEnd,
        toStart,
        toEnd
    );

export type MapAndScaleValueOptions = Readonly<{
    value: number;
    fromStart: number;
    fromEnd: number;
    toStart: number;
    toEnd: number;
    scale: 'linear' | 'log' | 'time';
    reverse?: boolean;
    constrain?: boolean;
}>;

export const mapAndScaleValue = ({
    value,
    fromStart,
    fromEnd,
    toStart,
    toEnd,
    scale,
    reverse = false,
    constrain = false,
}: MapAndScaleValueOptions) => {
    const mapper = constrain ? mapAndConstrainValue : mapValue;

    switch (scale) {
        case 'log':
        case 'time': {
            const logFromStart = log(fromStart);
            const logFromEnd = log(fromEnd);

            return reverse
                ? exp(mapper(value, toStart, toEnd, logFromStart, logFromEnd))
                : mapper(log(value), logFromStart, logFromEnd, toStart, toEnd);
        }
        case 'linear':
            return reverse
                ? mapper(value, toStart, toEnd, fromStart, fromEnd)
                : mapper(value, fromStart, fromEnd, toStart, toEnd);
        default:
            throw new Error('Invalid scale');
    }
};
export type GetDataTransformsOptions = Readonly<{
    axis: 'x' | 'y';
    range: ChartRange;
    zoom: ChartRange;
    axisScale: AxisScale;
    contentSize: number;
}>;

export const getDataTransforms = ({
    axis,
    range,
    zoom,
    axisScale,
    contentSize,
}: GetDataTransformsOptions) => {
    const isXAxis = axis === 'x';
    const isLog = axisScale === 'log';
    const rangeMin = isXAxis ? range.min : range.max;
    const rangeMax = isXAxis ? range.max : range.min;
    const zoomMin = isXAxis ? zoom.min : zoom.max;
    const zoomMax = isXAxis ? zoom.max : zoom.min;

    const finalRangeMin = isLog ? log(rangeMin) : rangeMin;
    const finalRangeMax = isLog ? log(rangeMax) : rangeMax;
    const finalZoomMin = isLog ? log(zoomMin) : zoomMin;
    const finalZoomMax = isLog ? log(zoomMax) : zoomMax;

    if (
        finalZoomMax === finalZoomMin ||
        (finalZoomMin === finalRangeMin && finalZoomMax === finalRangeMax)
    ) {
        return { scale: 1, translate: 0 };
    }

    const zoomSize = finalZoomMax - finalZoomMin;
    const scaleTransform = (finalRangeMax - finalRangeMin) / zoomSize;

    const translateTransform = -(
        (contentSize * (finalZoomMin - finalRangeMin)) /
        zoomSize
    );

    return { scale: scaleTransform, translate: translateTransform };
};

export const constrainZoomToRange = (zoom: ChartRange, range: ChartRange) => {
    if (chartRangesAreEqual(zoom, range)) {
        return zoom;
    }
    const min = constrainValue(zoom.min, range.min, range.max);
    const max = constrainValue(zoom.max, range.min, range.max);

    return { min, max };
};

export type MapDataPointOptions = Readonly<{
    width: number;
    height: number;
    dataPoint: Point;
    xAxisRange: ChartRange;
    xAxisScale: AxisScale;
    yAxisRange: ChartRange;
    yAxisScale: AxisScale;
}>;

export const mapDataPoint = ({
    width,
    height,
    dataPoint: { x, y },
    xAxisRange,
    xAxisScale,
    yAxisRange,
    yAxisScale,
}: MapDataPointOptions) => ({
    x: mapAndScaleValue({
        value: x,
        fromStart: xAxisRange.min,
        fromEnd: xAxisRange.max,
        toStart: 0,
        toEnd: width,
        scale: xAxisScale,
    }),
    y: mapAndScaleValue({
        value: y,
        fromStart: yAxisRange.min,
        fromEnd: yAxisRange.max,
        toStart: height,
        toEnd: 0,
        scale: yAxisScale,
    }),
});

export type MapDataPointsOptions = Readonly<{
    width: number;
    height: number;
    dataPoints: Point[];
    xAxisRange: ChartRange;
    xAxisScale: AxisScale;
    yAxisRange: ChartRange;
    yAxisScale: AxisScale;
}>;

export const mapDataPoints = ({
    width,
    height,
    dataPoints,
    xAxisRange,
    xAxisScale,
    yAxisRange,
    yAxisScale,
}: MapDataPointsOptions) =>
    dataPoints.map((dataPoint) =>
        mapDataPoint({
            width,
            height,
            dataPoint,
            xAxisRange,
            xAxisScale,
            yAxisRange,
            yAxisScale,
        })
    );

export const mapRangeByCount = <TMappedValue>(
    fromValue: number,
    toValue: number,
    rangeSize: number,
    mapper: (value: number) => TMappedValue
): TMappedValue[] => {
    if (rangeSize < 1) {
        return [];
    }
    if (rangeSize === 1 || fromValue === toValue) {
        return [];
    }
    if (rangeSize === 2) {
        return [mapper(fromValue), mapper(toValue)];
    }

    const step = (toValue - fromValue) / (rangeSize + 1);
    const values: TMappedValue[] = [];

    for (let value = fromValue; value < toValue; value += step) {
        values.push(mapper(value));
    }

    return [...values, mapper(toValue)];
};

export const mapRangeByStep = <TMappedValue>(
    fromValue: number,
    toValue: number,
    step: number,
    mapper: (value: number) => TMappedValue
) => {
    if (step === 0) {
        return [];
    }

    const rangeIsInverted = toValue < fromValue;
    const absStep = Math.abs(step);
    const values: TMappedValue[] = [];

    if (rangeIsInverted) {
        for (let value = fromValue; value >= toValue; value -= absStep) {
            values.push(mapper(value));
        }
    } else {
        for (let value = fromValue; value <= toValue; value += absStep) {
            values.push(mapper(value));
        }
    }

    return values;
};

export type GetPagePointOnElementOptions = Readonly<{
    element: Element;
    innerBounds?: Bounds;
    pageX: number;
    pageY: number;
}>;

export const getPagePointOnElement = ({
    element,
    innerBounds,
    pageX,
    pageY,
}: GetPagePointOnElementOptions): Point | undefined => {
    const elementBounds = new Bounds(element.getBoundingClientRect());
    const pointOnPage = { x: pageX, y: pageY };
    const pointInElement = {
        x: pageX - elementBounds.left,
        y: pageY - elementBounds.top,
    };

    if (innerBounds) {
        return innerBounds.containsPoint(pointInElement)
            ? {
                  x: pointInElement.x - innerBounds.left,
                  y: pointInElement.y - innerBounds.top,
              }
            : undefined;
    }

    return elementBounds.containsPoint(pointOnPage) ? pointInElement : undefined;
};

export type FindClosestPointsOptions = Readonly<{
    point: Point;
    bounds: Bounds;
    seriesDataPoints: AxisChartSeriesDataPoint[];
    leftAxisZoom: ChartRange;
    leftAxisScale: AxisScale;
    bottomAxisZoom: ChartRange;
    bottomAxisScale: AxisScale;
}>;

export const findClosestPoints = ({
    point,
    bounds,
    seriesDataPoints,
    leftAxisZoom,
    leftAxisScale,
    bottomAxisZoom,
    bottomAxisScale,
}: FindClosestPointsOptions): AxisChartSeriesDataPoint[] => {
    if (isChartRangeEmpty(leftAxisZoom) || isChartRangeEmpty(bottomAxisZoom)) {
        return [];
    }

    const { width, height } = bounds;

    return (
        seriesDataPoints.reduce<
            | {
                  distance: number;
                  seriesDataPoints: AxisChartSeriesDataPoint[];
              }
            | undefined
        >((result, seriesDataPoint) => {
            const { dataPoint } = seriesDataPoint;

            const { x: mappedX, y: mappedY } = mapDataPoint({
                width,
                height,
                dataPoint,
                xAxisRange: bottomAxisZoom,
                xAxisScale: bottomAxisScale,
                yAxisRange: leftAxisZoom,
                yAxisScale: leftAxisScale,
            });

            if (mappedX < 0 || mappedY < 0 || mappedX > width || mappedY > height) {
                return result;
            }

            const finalMappedX = mappedX + bounds.left;
            const finalMappedY = mappedY + bounds.top;

            const distance =
                (finalMappedX - point.x) ** 2 + (finalMappedY - point.y) ** 2;

            if (result === undefined || distance < result.distance) {
                return { distance, seriesDataPoints: [seriesDataPoint] };
            }

            if (distance === result.distance) {
                return {
                    ...result,
                    seriesDataPoints: [...result.seriesDataPoints, seriesDataPoint],
                };
            }

            return result;
        }, undefined)?.seriesDataPoints ?? []
    );
};
